import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Monitor, Gamepad2, Layers, Package, Building, Headphones, ArrowRight } from 'lucide-react';

const Solutions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const solutions = [
    {
      icon: Monitor,
      title: 'Premium Workspace Solutions',
      description:
        'Ergonomic, productivity-focused workspace setups designed for business travelers, remote workers, and hybrid environments. Complete with dual monitors, ergonomic chairs, proper lighting, and cable management.',
      features: ['Ergonomic furniture', 'Dual monitor setups', 'Premium peripherals', 'Cable management', 'Ambient lighting'],
      image: 'https://images.pexels.com/photos/8004074/pexels-photo-8004074.jpeg',
    },
    {
      icon: Gamepad2,
      title: 'Gaming Setups',
      description:
        'Elegant gaming configurations that balance performance with premium aesthetics. Designed for hospitality and commercial spaces without the aggressive gamer look.',
      features: ['High-performance hardware', 'Minimal aesthetics', 'Comfortable seating', 'Quality audio', 'Premium displays'],
      image: 'https://images.pexels.com/photos/7745560/pexels-photo-7745560.jpeg',
    },
    {
      icon: Layers,
      title: 'Smart Room Concepts',
      description:
        'Complete room solutions that integrate workspace, entertainment, and comfort. Modular designs that adapt to different user needs and spatial requirements.',
      features: ['Flexible layouts', 'Integrated technology', 'Space optimization', 'User-friendly controls', 'Seamless connectivity'],
      image: 'https://images.unsplash.com/photo-1765371512707-9e0e96fd9e5b',
    },
    {
      icon: Package,
      title: 'Modular Accessories',
      description:
        'Standardized, interchangeable product kits for easy maintenance, replacement, and scaling. Perfect for multi-location deployments.',
      features: ['Easy replacement', 'Standardized quality', 'Scalable inventory', 'Quick deployment', 'Minimal downtime'],
      image: 'https://images.unsplash.com/photo-1776919017122-8140e279c889',
    },
    {
      icon: Building,
      title: 'Scalable Commercial Environments',
      description:
        'Large-scale rollouts for hotels, co-living spaces, and commercial properties. We handle everything from concept design to installation and ongoing support.',
      features: ['Multi-location support', 'Consistent quality', 'Centralized procurement', 'Professional installation', 'Ongoing maintenance'],
      image: 'https://images.unsplash.com/photo-1774192621035-20d11389f781',
    },
  ];

  return (
    <div className="bg-[#FDFBF7] pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32" data-testid="solutions-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-['Outfit'] text-5xl sm:text-6xl tracking-tight leading-none text-[#0A1F38] mb-6">
              Premium Solutions for Modern Environments
            </h1>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl mx-auto">
              From complete workspace concepts to modular gaming setups, we create scalable solutions that
              combine premium design with practical functionality.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions List */}
      <section className="pb-24 sm:pb-32" data-testid="solutions-list">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="space-y-24">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`solution-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <solution.icon className="text-[#C5A05B] mb-6" size={56} strokeWidth={1.5} />
                  <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
                    {solution.title}
                  </h2>
                  <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-8">
                    {solution.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {solution.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-3">
                        <div className="w-2 h-2 rounded-full bg-[#C5A05B]"></div>
                        <span className="font-['Manrope'] text-base text-[#475569]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="rounded-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] w-full"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="solutions-cta">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
              Let's Create Your Perfect Setup
            </h2>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-10 max-w-2xl mx-auto">
              Ready to discuss your project? We'll help you design and implement solutions tailored to your
              environment.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-10 py-5 rounded-full bg-[#0A1F38] text-[#FDFBF7] font-['Manrope'] font-semibold text-lg transition-all duration-300 hover:bg-[#0A1F38] hover:shadow-[0_20px_40px_rgba(0,0,0,0.15)] group"
              data-testid="solutions-contact-cta"
            >
              Get in Touch
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={24} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;